import { faKeyboard, faList, faUpload } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
// @ts-ignore - FileUploaderItem is exported at runtime but missing from @wfp/ui's index.d.ts
import { FileUploaderItem, Modal, TextInput } from "@wfp/ui"
import Button from "components/Shared/Button/Button"
import useCommon from "hooks/useCommon"
import { useEffect, useMemo, useRef, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { useDebounce } from "react-use"
import Select from "react-select"
import {
  AnalysisType,
  backendAssetToFrontend,
  formValuesToAssetCreate,
  useAttachAssetPolygonMutation,
  useCreateAssetMutation,
  useGetAssetQuery,
  useGetAnalysisTypesQuery,
  useListAdmin1Query,
  useListAdmin2Query,
  useListAdmin3Query,
  useListAssetCategoriesQuery,
  useListAssetDetailsQuery,
  useListAssetOutcomesQuery,
  useListAssetSubcategoriesQuery,
  useListCountriesQuery,
  usePatchAssetMutation,
  useValidateAssetLocationMutation,
} from "services/aimsBackend"

type Option = { value: string; label: string }
type AdminSelectOption = { value: number; label: string }
type LocationValidationState = {
  status: "idle" | "checking" | "valid" | "invalid"
  key: string
  message: string
}

// Shared react-select styles: truncate the selected value with an ellipsis
// and constrain the dropdown menu to the input width so long labels don't
// overflow the form column.
const truncatingSelectStyles: any = {
  container: (base: any) => ({ ...base, minWidth: 0, maxWidth: "100%" }),
  control: (base: any) => ({ ...base, minWidth: 0 }),
  valueContainer: (base: any) => ({ ...base, flexWrap: "nowrap", minWidth: 0, overflow: "hidden" }),
  singleValue: (base: any) => ({
    ...base,
    maxWidth: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  }),
  menu: (base: any) => ({ ...base, width: "100%", maxWidth: "100%" }),
  menuList: (base: any) => ({ ...base, maxHeight: 240 }),
  option: (base: any) => ({
    ...base,
    whiteSpace: "normal",
    wordBreak: "break-word",
  }),
}

const errorSelectStyles = (hasError: boolean): any =>
  hasError
    ? {
        ...truncatingSelectStyles,
        control: (base: any) => ({ ...truncatingSelectStyles.control(base), borderColor: "#da1e28" }),
      }
    : truncatingSelectStyles

const monthOptions: Option[] = [
  { value: "Jan", label: "Jan" },
  { value: "Feb", label: "Feb" },
  { value: "Mar", label: "Mar" },
  { value: "Apr", label: "Apr" },
  { value: "May", label: "May" },
  { value: "Jun", label: "Jun" },
  { value: "Jul", label: "Jul" },
  { value: "Aug", label: "Aug" },
  { value: "Sep", label: "Sep" },
  { value: "Oct", label: "Oct" },
  { value: "Nov", label: "Nov" },
  { value: "Dec", label: "Dec" },
]

const monthAliases: Record<string, string> = {
  jan: "Jan",
  january: "Jan",
  feb: "Feb",
  february: "Feb",
  mar: "Mar",
  march: "Mar",
  apr: "Apr",
  april: "Apr",
  may: "May",
  jun: "Jun",
  june: "Jun",
  jul: "Jul",
  july: "Jul",
  aug: "Aug",
  august: "Aug",
  sep: "Sep",
  sept: "Sep",
  september: "Sep",
  oct: "Oct",
  october: "Oct",
  nov: "Nov",
  november: "Nov",
  dec: "Dec",
  december: "Dec",
}

const normalizeMonth = (value: string): string => monthAliases[value.trim().toLowerCase()] ?? value

const getMonthOptionsForValue = (value: string): Option[] => {
  if (!value || monthOptions.some(option => option.value === value)) return monthOptions
  const normalized = normalizeMonth(value)
  if (!monthOptions.some(option => option.value === normalized)) return [{ value, label: value }, ...monthOptions]
  return monthOptions.map(option => (option.value === normalized ? { value, label: value } : option))
}

const getSelectedMonthOption = (value: string): Option | null => {
  if (!value) return null
  const exact = monthOptions.find(option => option.value === value)
  if (exact) return exact
  const normalized = normalizeMonth(value)
  if (monthOptions.some(option => option.value === normalized)) return { value, label: value }
  return { value, label: value }
}

const getSelectedAdminOption = (
  options: AdminSelectOption[],
  code: number | null,
  savedValue: string,
): AdminSelectOption | null => {
  const selectedByCode = code != null ? options.find(option => option.value === code) : null
  if (selectedByCode) return selectedByCode
  const selectedByLabel = options.find(option => option.label.trim().toLowerCase() === savedValue.trim().toLowerCase())
  if (selectedByLabel) return selectedByLabel
  return savedValue ? { value: code ?? -1, label: savedValue } : null
}

const emptyAsset = {
  country: "",
  fla: "",
  admin1: "",
  admin2: "",
  community: "",
  analysis: "",
  latY: "",
  longX: "",
  assetType: "",
  assetSubcat: "",
  assetDetail: "",
  prjSize: "",
  outputUnit: "",
  startMonth: "",
  startYear: "",
  endMonth: "",
  endYear: "",
  villages: "",
  outcomes: "",
  impactArea: "",
  extraInfo: "",
}

type AssetFormValues = typeof emptyAsset

const getAssetFormErrors = (values: AssetFormValues, requireCountry: boolean) => {
  const nextErrors: Record<string, string> = {}
  const requiredFields = [
    ...(requireCountry ? [["country", "Country is required"]] : []),
    ["admin1", "Admin1 is required"],
    ["admin2", "Admin2 is required"],
    ["analysis", "Analysis is required"],
    ["latY", "Lat_Y is required"],
    ["longX", "Long_X is required"],
    ["assetType", "AssetType is required"],
    ["assetSubcat", "AssetSubcat is required"],
    ["assetDetail", "AssetDetail is required"],
    ["startMonth", "StartMonth is required"],
    ["startYear", "StartYear is required"],
    ["endMonth", "EndMonth is required"],
    ["endYear", "EndYear is required"],
    ["outcomes", "Outcomes is required"],
  ]

  requiredFields.forEach(([key, message]) => {
    if (!String(values[key as keyof AssetFormValues]).trim()) nextErrors[key] = message
  })

  if (!nextErrors.latY) {
    const lat = Number(values.latY)
    if (Number.isNaN(lat)) nextErrors.latY = "Lat_Y must be a valid number"
    else if (lat < -90 || lat > 90) nextErrors.latY = "Lat_Y must be between -90 and 90"
  }
  if (!nextErrors.longX) {
    const lng = Number(values.longX)
    if (Number.isNaN(lng)) nextErrors.longX = "Long_X must be a valid number"
    else if (lng < -180 || lng > 180) nextErrors.longX = "Long_X must be between -180 and 180"
  }
  if (!nextErrors.startYear) {
    const year = Number(values.startYear)
    if (!Number.isInteger(year) || year < 1900 || year > 2100)
      nextErrors.startYear = "StartYear must be a valid year (1900-2100)"
  }
  if (!nextErrors.endYear) {
    const year = Number(values.endYear)
    if (!Number.isInteger(year) || year < 1900 || year > 2100)
      nextErrors.endYear = "EndYear must be a valid year (1900-2100)"
  }
  if (!nextErrors.startYear && !nextErrors.endYear) {
    const startYear = Number(values.startYear)
    const endYear = Number(values.endYear)
    if (endYear < startYear) nextErrors.endYear = "EndYear must be greater than or equal to StartYear"
  }

  return nextErrors
}

const toggleButtonStyle: React.CSSProperties = {
  cursor: "pointer",
  width: 38,
  height: 38,
  border: "1px solid #8c8c8c",
  borderRadius: 4,
  background: "#fff",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
}

function RequiredLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="m-0 mb-1">
      {children}
      <span aria-label="required" style={{ color: "#da1e28" }}>
        {" "}
        *
      </span>
    </p>
  )
}

export default function AssetFormPage() {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const { showSnackBar } = useCommon()

  const [createAsset, { isLoading: isCreating }] = useCreateAssetMutation()
  const [patchAsset, { isLoading: isUpdating }] = usePatchAssetMutation()
  const [attachAssetPolygon, { isLoading: isAttachingPolygon }] = useAttachAssetPolygonMutation()
  const [validateAssetLocation] = useValidateAssetLocationMutation()
  const assetId = Number(id)
  const shouldFetchAsset = Boolean(id) && Number.isFinite(assetId)
  const { data: backendAsset } = useGetAssetQuery(assetId, { skip: !shouldFetchAsset })

  // Vocabulary lookups (backend-managed)
  const { data: categories = [] } = useListAssetCategoriesQuery()
  const { data: outcomesList = [] } = useListAssetOutcomesQuery()
  const { data: analysisTypes } = useGetAnalysisTypesQuery()
  const { data: countriesList = [] } = useListCountriesQuery()

  const existingAsset = useMemo(() => (backendAsset ? backendAssetToFrontend(backendAsset) : null), [backendAsset])
  const isEditMode = !!id && !!existingAsset

  // ── Form state ───────────────────────────────────────────────────
  const initialValues = useMemo(() => {
    if (!existingAsset) return emptyAsset
    return {
      fla: existingAsset.fla,
      country: existingAsset.country,
      admin1: existingAsset.admin1,
      admin2: existingAsset.admin2,
      community: existingAsset.community,
      analysis: existingAsset.analysis,
      latY: String(existingAsset.latY),
      longX: String(existingAsset.longX),
      assetType: existingAsset.assetType,
      assetSubcat: existingAsset.assetSubcat,
      assetDetail: existingAsset.assetDetail,
      prjSize: String(existingAsset.prjSize || ""),
      outputUnit: existingAsset.outputUnit,
      startMonth: existingAsset.startMonth,
      startYear: String(existingAsset.startYear),
      endMonth: existingAsset.endMonth,
      endYear: String(existingAsset.endYear),
      villages: existingAsset.villages,
      outcomes: existingAsset.outcomes,
      impactArea: existingAsset.impactArea,
      extraInfo: existingAsset.extraInfo,
    }
  }, [existingAsset])

  const [values, setValues] = useState(initialValues)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [showConfirm, setShowConfirm] = useState(false)
  const [kmzFile, setKmzFile] = useState<File | undefined>(undefined)
  const [locationValidation, setLocationValidation] = useState<LocationValidationState>({
    status: "idle",
    key: "",
    message: "",
  })
  const kmzInputRef = useRef<HTMLInputElement>(null)

  // Cascading selector ids (not part of the payload)
  const [categoryId, setCategoryId] = useState<number | null>(null)
  const [subcategoryId, setSubcategoryId] = useState<number | null>(null)
  // Admin cascade — codes drive child queries; the names are what we send to the API
  const [admin1Code, setAdmin1Code] = useState<number | null>(null)
  const [admin2Code, setAdmin2Code] = useState<number | null>(null)

  // "select" vs free-text toggle for fields that allow both
  const [assetDetailMode, setAssetDetailMode] = useState<"select" | "text">("select")
  const [outcomesMode, setOutcomesMode] = useState<"select" | "text">("select")

  useEffect(() => {
    setValues(initialValues)
  }, [initialValues])

  // Re-sync the cascading ids when the form loads an existing asset (edit mode)
  useEffect(() => {
    if (!existingAsset) return
    const cat = categories.find(c => c.title === existingAsset.assetType)
    setCategoryId(cat?.id ?? null)
  }, [existingAsset, categories])

  // ── Cascading vocabulary queries ─────────────────────────────────
  const { data: subcategories = [] } = useListAssetSubcategoriesQuery(
    categoryId ? { category: categoryId } : ({} as any),
    { skip: !categoryId },
  )
  const { data: assetDetails = [] } = useListAssetDetailsQuery(
    subcategoryId ? { subcategory: subcategoryId } : ({} as any),
    { skip: !subcategoryId },
  )

  // ── Cascading admin queries ──────────────────────────────────────
  const { data: admin1Items = [] } = useListAdmin1Query()
  const { data: admin2Items = [] } = useListAdmin2Query(admin1Code != null ? { adm1_code: admin1Code } : ({} as any), {
    skip: admin1Code == null,
  })
  const { data: admin3Items = [] } = useListAdmin3Query(admin2Code != null ? { adm2_code: admin2Code } : ({} as any), {
    skip: admin2Code == null,
  })

  // ── Derived dropdown options ─────────────────────────────────────
  // Admin options: keep value as-is so onChange has the numeric code; we
  // store the code separately and send the name string to the API.
  const admin1Options = useMemo(() => admin1Items.map(a => ({ value: a.value, label: a.name })), [admin1Items])
  const admin2Options = useMemo(() => admin2Items.map(a => ({ value: a.value, label: a.name })), [admin2Items])
  const admin3Options = useMemo(() => admin3Items.map(a => ({ value: a.value, label: a.name })), [admin3Items])
  const countryOptions = useMemo<Option[]>(
    () => countriesList.map(country => ({ value: country.iso3, label: country.name })),
    [countriesList],
  )

  useEffect(() => {
    if (!isEditMode || !values.admin1 || admin1Code != null) return
    const match = admin1Options.find(option => option.label.trim().toLowerCase() === values.admin1.trim().toLowerCase())
    if (match) setAdmin1Code(match.value)
  }, [admin1Code, admin1Options, isEditMode, values.admin1])

  useEffect(() => {
    if (!isEditMode || !values.admin2 || admin2Code != null) return
    const match = admin2Options.find(option => option.label.trim().toLowerCase() === values.admin2.trim().toLowerCase())
    if (match) setAdmin2Code(match.value)
  }, [admin2Code, admin2Options, isEditMode, values.admin2])

  const assetTypeOptions = useMemo<Option[]>(
    () => categories.map(c => ({ value: c.title, label: c.title })),
    [categories],
  )
  const assetSubcatOptions = useMemo<Option[]>(
    () => subcategories.map(s => ({ value: s.title, label: s.title })),
    [subcategories],
  )
  const assetDetailOptions = useMemo<Option[]>(
    () => assetDetails.map(d => ({ value: d.title, label: d.title })),
    [assetDetails],
  )
  const outcomeOptions = useMemo<Option[]>(
    () => outcomesList.map(o => ({ value: o.title, label: o.title })),
    [outcomesList],
  )
  const analysisOptions = useMemo<Option[]>(
    () => (analysisTypes ?? []).map(a => ({ value: a.value, label: a.name })),
    [analysisTypes],
  )
  const startMonthOptions = useMemo(() => getMonthOptionsForValue(values.startMonth), [values.startMonth])
  const endMonthOptions = useMemo(() => getMonthOptionsForValue(values.endMonth), [values.endMonth])
  const canAttachPolygon = values.analysis === "LIA" || values.analysis === "ADA/LIA"
  const countryForValidation = values.country || existingAsset?.country || ""
  const latValue = values.latY.trim()
  const lngValue = values.longX.trim()
  const latNumber = Number(latValue)
  const lngNumber = Number(lngValue)
  const hasLatValue = latValue !== ""
  const hasLngValue = lngValue !== ""
  const isLatNumber = !Number.isNaN(latNumber)
  const isLngNumber = !Number.isNaN(lngNumber)
  const isLatInRange = latNumber >= -90 && latNumber <= 90
  const isLngInRange = lngNumber >= -180 && lngNumber <= 180
  const coordinatesChanged =
    !isEditMode ||
    !existingAsset ||
    Number(existingAsset.latY) !== latNumber ||
    Number(existingAsset.longX) !== lngNumber
  const locationValidationKey = `${countryForValidation}|${values.latY}|${values.longX}`
  const isLatFormatValid = hasLatValue && isLatNumber && isLatInRange
  const isLngFormatValid = hasLngValue && isLngNumber && isLngInRange
  const isLocationReady =
    !coordinatesChanged || (locationValidation.status === "valid" && locationValidation.key === locationValidationKey)
  const formErrors = useMemo(() => getAssetFormErrors(values, !isEditMode), [isEditMode, values])
  const isFormValid = Object.keys(formErrors).length === 0 && isLocationReady

  // In edit mode, drop into "text" mode if the existing value isn't in the
  // current options list — that way users see exactly what was saved.
  useEffect(() => {
    if (!isEditMode || !existingAsset) return
    const detailMatch = assetDetailOptions.some(o => o.value === existingAsset.assetDetail)
    setAssetDetailMode(detailMatch ? "select" : "text")
    const outcomeMatch = outcomeOptions.some(o => o.value === existingAsset.outcomes)
    setOutcomesMode(outcomeMatch ? "select" : "text")
  }, [isEditMode, existingAsset, assetDetailOptions, outcomeOptions])

  useEffect(() => {
    if (canAttachPolygon) return
    setKmzFile(undefined)
    if (kmzInputRef.current) kmzInputRef.current.value = ""
  }, [canAttachPolygon])

  // ── Helpers ──────────────────────────────────────────────────────
  const setField = (key: string, value: string) => {
    setValues(prev => ({ ...prev, [key]: value }))
    if (errors[key]) setErrors(prev => ({ ...prev, [key]: "" }))
  }

  const setLocationErrorsFromApi = (data: any) => {
    if (!Array.isArray(data?.errors)) return
    const fieldMap: Record<string, keyof typeof values> = {
      Country: "country",
      country: "country",
      Lat_Y: "latY",
      Long_X: "longX",
    }
    setErrors(prev => {
      const next = { ...prev }
      data.errors.forEach((item: any) => {
        const field = fieldMap[item.field]
        if (field) next[field] = String(item.error || "invalid")
      })
      return next
    })
  }

  useDebounce(
    () => {
      if (!coordinatesChanged) {
        setLocationValidation({ status: "valid", key: locationValidationKey, message: "" })
        return
      }

      if (!hasLatValue || !hasLngValue) {
        setLocationValidation({ status: "idle", key: locationValidationKey, message: "" })
        return
      }

      if (!isLatFormatValid || !isLngFormatValid) {
        setLocationValidation({
          status: "invalid",
          key: locationValidationKey,
          message: "Enter valid latitude and longitude before saving.",
        })
        return
      }

      if (!countryForValidation) {
        setLocationValidation({
          status: "invalid",
          key: locationValidationKey,
          message: "Country is required to validate coordinates.",
        })
        return
      }

      setLocationValidation({ status: "checking", key: locationValidationKey, message: "Checking coordinates..." })
      validateAssetLocation({
        country: countryForValidation,
        lat_y: latNumber,
        long_x: lngNumber,
      })
        .unwrap()
        .then(() => {
          setLocationValidation({ status: "valid", key: locationValidationKey, message: "Coordinates are valid." })
          setErrors(prev => {
            const next = { ...prev }
            delete next.country
            delete next.latY
            delete next.longX
            return next
          })
        })
        .catch((err: any) => {
          setLocationValidation({
            status: "invalid",
            key: locationValidationKey,
            message: "Coordinates do not match the selected country.",
          })
          setLocationErrorsFromApi(err?.data)
        })
    },
    600,
    [
      coordinatesChanged,
      countryForValidation,
      hasLatValue,
      hasLngValue,
      isLatFormatValid,
      isLngFormatValid,
      latNumber,
      lngNumber,
      locationValidationKey,
      validateAssetLocation,
    ],
  )

  const validate = () => {
    const nextErrors = getAssetFormErrors(values, !isEditMode)
    setErrors(nextErrors)
    return Object.keys(nextErrors).length === 0
  }

  const onSave = async () => {
    if (!isLocationReady) {
      showSnackBar(locationValidation.message || "Wait for coordinate validation to finish.", "error")
      return
    }

    const lat = Number(values.latY || 0)
    const lng = Number(values.longX || 0)
    const payload = formValuesToAssetCreate({
      fla: values.fla,
      country: values.country,
      admin1: values.admin1,
      admin2: values.admin2,
      community: values.community,
      analysis: values.analysis as AnalysisType,
      latY: lat,
      longX: lng,
      assetType: values.assetType,
      assetSubcat: values.assetSubcat,
      assetDetail: values.assetDetail,
      prjSize: values.prjSize ? Number(values.prjSize) : undefined,
      outputUnit: values.outputUnit,
      startMonth: values.startMonth,
      startYear: values.startYear,
      endMonth: values.endMonth,
      endYear: values.endYear,
      villages: values.villages,
      outcomes: values.outcomes,
      impactArea: values.impactArea,
      extraInfo: values.extraInfo,
    })

    try {
      if (isEditMode && existingAsset) {
        await patchAsset({ id: existingAsset.id, body: payload }).unwrap()
        if (kmzFile && canAttachPolygon) {
          await attachAssetPolygon({ id: existingAsset.id, kmz: kmzFile }).unwrap()
        }
        showSnackBar(
          kmzFile && canAttachPolygon ? "Asset and polygon updated successfully" : "Asset updated successfully",
          "success",
        )
      } else {
        const created = await createAsset(payload).unwrap()
        if (kmzFile && canAttachPolygon) {
          await attachAssetPolygon({ id: created.detail.asset_id, kmz: kmzFile }).unwrap()
        }
        if (canAttachPolygon && !kmzFile) {
          showSnackBar(
            "Asset created, but no polygon was uploaded. Upload a .kmz polygon before assigning an LIA contract.",
            "warning",
          )
        } else {
          showSnackBar(
            kmzFile && canAttachPolygon ? "Asset and polygon created successfully" : "Asset created successfully",
            "success",
          )
        }
      }
      navigate("/aims/AssetsView")
    } catch (err: any) {
      const data = err?.data
      const summary = data?.error_summary || data?.errors || data?.detail
      if (Array.isArray(data?.errors)) {
        setLocationErrorsFromApi(data)
      }
      const message = Array.isArray(summary)
        ? summary.map((item: any) => `${item.field}: ${item.error}`).join("; ")
        : typeof summary === "string"
        ? summary
        : summary && typeof summary === "object"
        ? Object.entries(summary)
            .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(", ") : v}`)
            .join("; ")
        : "Failed to create asset. Please review the fields and try again."
      showSnackBar(message, "error")
    } finally {
      setShowConfirm(false)
    }
  }

  // ── Cascading change handlers ────────────────────────────────────
  const onPickCategory = (option: Option | null) => {
    setField("assetType", option?.value ?? "")
    setField("assetSubcat", "")
    setField("assetDetail", "")
    setSubcategoryId(null)
    const cat = option ? categories.find(c => c.title === option.value) ?? null : null
    setCategoryId(cat?.id ?? null)
  }

  const onPickSubcategory = (option: Option | null) => {
    setField("assetSubcat", option?.value ?? "")
    setField("assetDetail", "")
    const sub = option ? subcategories.find(s => s.title === option.value) ?? null : null
    setSubcategoryId(sub?.id ?? null)
  }

  const onPickAdmin1 = (option: { value: number; label: string } | null) => {
    setAdmin1Code(option?.value ?? null)
    setField("admin1", option?.label ?? "")
    setAdmin2Code(null)
    setField("admin2", "")
    setField("community", "")
  }

  const onPickAdmin2 = (option: { value: number; label: string } | null) => {
    setAdmin2Code(option?.value ?? null)
    setField("admin2", option?.label ?? "")
    setField("community", "")
  }

  const onPickCommunity = (option: { value: number; label: string } | null) => {
    setField("community", option?.label ?? "")
  }

  const isSaving = isCreating || isUpdating || isAttachingPolygon
  const isSaveDisabled = isSaving || locationValidation.status === "checking" || !isFormValid
  const saveLabel =
    locationValidation.status === "checking" ? "Checking coordinates..." : isSaving ? "Saving..." : "Save"

  return (
    <div className="grid grid-cols-2 gap-4 mt-2">
      <div className="bg-white border border-[#d8d8d8] rounded-md p-4" style={{ minWidth: 0 }}>
        <h2 className="m-0 mb-3">{isEditMode ? "Edit Asset" : "Add Asset"}</h2>
        {!isEditMode && (
          <div className="mb-3">
            <RequiredLabel>Country / Territory</RequiredLabel>
            <Select
              classNamePrefix="wfp--react-select"
              className="wfp--react-select-container"
              options={countryOptions}
              value={countryOptions.find(item => item.value === values.country) || null}
              onChange={option => setField("country", option?.value || "")}
              placeholder="Select country or territory"
              isClearable
              isSearchable
              styles={errorSelectStyles(!!errors.country)}
            />
            {errors.country && (
              <p className="m-0 mt-1" style={{ color: "#da1e28", fontSize: 12 }}>
                {errors.country}
              </p>
            )}
          </div>
        )}

        <div className="mb-3">
          <p className="m-0 mb-1">FLA</p>
          <TextInput labelText="FLA" hideLabel value={values.fla} onChange={e => setField("fla", e.target.value)} />
        </div>

        <div className="mb-3">
          <RequiredLabel>Admin1</RequiredLabel>
          <Select
            classNamePrefix="wfp--react-select"
            className="wfp--react-select-container"
            options={admin1Options}
            value={getSelectedAdminOption(admin1Options, admin1Code, values.admin1)}
            onChange={onPickAdmin1}
            placeholder="Select admin 1"
            isClearable
            isSearchable
            styles={errorSelectStyles(!!errors.admin1)}
          />
          {errors.admin1 && (
            <p className="m-0 mt-1" style={{ color: "#da1e28", fontSize: 12 }}>
              {errors.admin1}
            </p>
          )}
        </div>

        <div className="mb-3">
          <RequiredLabel>Admin2</RequiredLabel>
          <Select
            classNamePrefix="wfp--react-select"
            className="wfp--react-select-container"
            options={admin2Options}
            value={getSelectedAdminOption(admin2Options, admin2Code, values.admin2)}
            onChange={onPickAdmin2}
            placeholder={admin1Code != null || values.admin1 ? "Select admin 2" : "Select admin 1 first"}
            isDisabled={admin1Code == null && !values.admin2}
            isClearable
            isSearchable
            styles={errorSelectStyles(!!errors.admin2)}
          />
          {errors.admin2 && (
            <p className="m-0 mt-1" style={{ color: "#da1e28", fontSize: 12 }}>
              {errors.admin2}
            </p>
          )}
        </div>

        <div className="mb-3">
          <p className="m-0 mb-1">Community</p>
          <Select
            classNamePrefix="wfp--react-select"
            className="wfp--react-select-container"
            options={admin3Options}
            value={getSelectedAdminOption(admin3Options, null, values.community)}
            onChange={onPickCommunity}
            placeholder={admin2Code != null || values.admin2 ? "Select community" : "Select admin 2 first"}
            isDisabled={admin2Code == null && !values.community}
            isClearable
            isSearchable
            styles={errorSelectStyles(false)}
          />
        </div>

        <div className="mb-3">
          <RequiredLabel>Analysis</RequiredLabel>
          <Select
            classNamePrefix="wfp--react-select"
            className="wfp--react-select-container"
            options={analysisOptions}
            value={analysisOptions.find(item => item.value === values.analysis) || null}
            onChange={option => setField("analysis", option?.value || "")}
            styles={errorSelectStyles(!!errors.analysis)}
          />
          {errors.analysis && (
            <p className="m-0 mt-1" style={{ color: "#da1e28", fontSize: 12 }}>
              {errors.analysis}
            </p>
          )}
        </div>

        {canAttachPolygon && (
          <div className="mb-3">
            <p className="m-0 mb-1">Polygon KMZ</p>
            <p className="m-0 mb-2" style={{ fontSize: 12, color: "#5a6872" }}>
              Optional. The polygon is validated after the asset is saved.
            </p>
            <input
              ref={kmzInputRef}
              type="file"
              accept=".kmz"
              style={{ display: "none" }}
              onChange={e => setKmzFile(e.target.files?.[0])}
            />
            {kmzFile ? (
              <FileUploaderItem
                uuid={kmzFile.name}
                name={kmzFile.name}
                status="edit"
                iconDescription="Remove file"
                onDelete={() => {
                  setKmzFile(undefined)
                  if (kmzInputRef.current) kmzInputRef.current.value = ""
                }}
              />
            ) : (
              <button
                type="button"
                onClick={() => kmzInputRef.current?.click()}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "6px 12px",
                  border: "1px solid #007dbc",
                  borderRadius: 4,
                  background: "#fff",
                  color: "#007dbc",
                  cursor: "pointer",
                  fontWeight: 600,
                }}
              >
                <FontAwesomeIcon icon={faUpload} />
                Choose .kmz file
              </button>
            )}
          </div>
        )}

        <div className="mb-3">
          <RequiredLabel>Lat_Y</RequiredLabel>
          <TextInput
            labelText="Lat_Y"
            hideLabel
            value={values.latY}
            onChange={e => setField("latY", e.target.value)}
            invalid={!!errors.latY}
            invalidText={errors.latY}
          />
        </div>
        <div className="mb-3">
          <RequiredLabel>Long_X</RequiredLabel>
          <TextInput
            labelText="Long_X"
            hideLabel
            value={values.longX}
            onChange={e => setField("longX", e.target.value)}
            invalid={!!errors.longX}
            invalidText={errors.longX}
          />
          {coordinatesChanged && locationValidation.status !== "idle" && (
            <p
              className="m-0 mt-1"
              style={{
                color:
                  locationValidation.status === "valid"
                    ? "#2e7d32"
                    : locationValidation.status === "checking"
                    ? "#007dbc"
                    : "#da1e28",
                fontSize: 12,
              }}
            >
              {locationValidation.message}
            </p>
          )}
        </div>
        <div className="mb-3">
          <p className="m-0 mb-1">Villages</p>
          <TextInput
            labelText="Villages"
            hideLabel
            value={values.villages}
            onChange={e => setField("villages", e.target.value)}
          />
        </div>
      </div>

      <div className="bg-white border border-[#d8d8d8] rounded-md p-4" style={{ minWidth: 0 }}>
        <h2 className="m-0 mb-3">Asset & Timing</h2>

        <div className="mb-3">
          <RequiredLabel>AssetType</RequiredLabel>
          <Select
            classNamePrefix="wfp--react-select"
            className="wfp--react-select-container"
            options={assetTypeOptions}
            value={assetTypeOptions.find(item => item.value === values.assetType) || null}
            onChange={onPickCategory}
            styles={errorSelectStyles(!!errors.assetType)}
          />
          {errors.assetType && (
            <p className="m-0 mt-1" style={{ color: "#da1e28", fontSize: 12 }}>
              {errors.assetType}
            </p>
          )}
        </div>

        <div className="mb-3">
          <RequiredLabel>AssetSubcat</RequiredLabel>
          <Select
            classNamePrefix="wfp--react-select"
            className="wfp--react-select-container"
            options={assetSubcatOptions}
            value={assetSubcatOptions.find(item => item.value === values.assetSubcat) || null}
            onChange={onPickSubcategory}
            isDisabled={!categoryId}
            placeholder={categoryId ? "Select subcategory" : "Select an asset type first"}
            styles={errorSelectStyles(!!errors.assetSubcat)}
          />
          {errors.assetSubcat && (
            <p className="m-0 mt-1" style={{ color: "#da1e28", fontSize: 12 }}>
              {errors.assetSubcat}
            </p>
          )}
        </div>

        <div className="mb-3">
          <RequiredLabel>AssetDetail</RequiredLabel>
          <div className="d-flex" style={{ gap: 8 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              {assetDetailMode === "select" ? (
                <Select
                  isSearchable
                  classNamePrefix="wfp--react-select"
                  className="wfp--react-select-container"
                  options={assetDetailOptions}
                  value={assetDetailOptions.find(item => item.value === values.assetDetail) || null}
                  onChange={option => setField("assetDetail", option?.value || "")}
                  placeholder={subcategoryId ? "Select asset detail" : "Select a subcategory first"}
                  isDisabled={!subcategoryId}
                  styles={errorSelectStyles(!!errors.assetDetail)}
                />
              ) : (
                <TextInput
                  labelText="AssetDetail"
                  hideLabel
                  value={values.assetDetail}
                  onChange={e => setField("assetDetail", e.target.value)}
                  invalid={!!errors.assetDetail}
                  invalidText={errors.assetDetail}
                />
              )}
            </div>
            <button
              type="button"
              onClick={() => setAssetDetailMode(prev => (prev === "select" ? "text" : "select"))}
              title={assetDetailMode === "select" ? "Switch to free text" : "Switch to suggestions"}
              style={toggleButtonStyle}
            >
              <FontAwesomeIcon icon={assetDetailMode === "select" ? faKeyboard : faList} />
            </button>
          </div>
          {errors.assetDetail && assetDetailMode === "select" && (
            <p className="m-0 mt-1" style={{ color: "#da1e28", fontSize: 12 }}>
              {errors.assetDetail}
            </p>
          )}
        </div>

        <div className="mb-3">
          <p className="m-0 mb-1">Prj_Size</p>
          <TextInput
            labelText="Prj_Size"
            hideLabel
            value={values.prjSize}
            onChange={e => setField("prjSize", e.target.value)}
          />
        </div>
        <div className="mb-3">
          <p className="m-0 mb-1">OutputUnit</p>
          <TextInput
            labelText="OutputUnit"
            hideLabel
            value={values.outputUnit}
            onChange={e => setField("outputUnit", e.target.value)}
          />
        </div>
        <div className="mb-3">
          <RequiredLabel>StartMonth</RequiredLabel>
          <Select
            classNamePrefix="wfp--react-select"
            className="wfp--react-select-container"
            options={startMonthOptions}
            value={getSelectedMonthOption(values.startMonth)}
            onChange={option => setField("startMonth", option?.value || "")}
            styles={errorSelectStyles(!!errors.startMonth)}
          />
          {errors.startMonth && (
            <p className="m-0 mt-1" style={{ color: "#da1e28", fontSize: 12 }}>
              {errors.startMonth}
            </p>
          )}
        </div>
        <div className="mb-3">
          <RequiredLabel>StartYear</RequiredLabel>
          <TextInput
            labelText="StartYear"
            hideLabel
            value={values.startYear}
            onChange={e => setField("startYear", e.target.value)}
            invalid={!!errors.startYear}
            invalidText={errors.startYear}
          />
        </div>
        <div className="mb-3">
          <RequiredLabel>EndMonth</RequiredLabel>
          <Select
            classNamePrefix="wfp--react-select"
            className="wfp--react-select-container"
            options={endMonthOptions}
            value={getSelectedMonthOption(values.endMonth)}
            onChange={option => setField("endMonth", option?.value || "")}
            styles={errorSelectStyles(!!errors.endMonth)}
          />
          {errors.endMonth && (
            <p className="m-0 mt-1" style={{ color: "#da1e28", fontSize: 12 }}>
              {errors.endMonth}
            </p>
          )}
        </div>
        <div className="mb-3">
          <RequiredLabel>EndYear</RequiredLabel>
          <TextInput
            labelText="EndYear"
            hideLabel
            value={values.endYear}
            onChange={e => setField("endYear", e.target.value)}
            invalid={!!errors.endYear}
            invalidText={errors.endYear}
          />
        </div>

        <div className="mb-3">
          <RequiredLabel>Outcomes</RequiredLabel>
          <div className="d-flex" style={{ gap: 8 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              {outcomesMode === "select" ? (
                <Select
                  isSearchable
                  classNamePrefix="wfp--react-select"
                  className="wfp--react-select-container"
                  options={outcomeOptions}
                  value={outcomeOptions.find(item => item.value === values.outcomes) || null}
                  onChange={option => setField("outcomes", option?.value || "")}
                  styles={errorSelectStyles(!!errors.outcomes)}
                />
              ) : (
                <TextInput
                  labelText="Outcomes"
                  hideLabel
                  value={values.outcomes}
                  onChange={e => setField("outcomes", e.target.value)}
                  invalid={!!errors.outcomes}
                  invalidText={errors.outcomes}
                />
              )}
            </div>
            <button
              type="button"
              onClick={() => setOutcomesMode(prev => (prev === "select" ? "text" : "select"))}
              title={outcomesMode === "select" ? "Switch to free text" : "Switch to suggestions"}
              style={toggleButtonStyle}
            >
              <FontAwesomeIcon icon={outcomesMode === "select" ? faKeyboard : faList} />
            </button>
          </div>
          {errors.outcomes && outcomesMode === "select" && (
            <p className="m-0 mt-1" style={{ color: "#da1e28", fontSize: 12 }}>
              {errors.outcomes}
            </p>
          )}
        </div>

        <div className="mb-3">
          <p className="m-0 mb-1">ImpactArea</p>
          <TextInput
            labelText="ImpactArea"
            hideLabel
            value={values.impactArea}
            onChange={e => setField("impactArea", e.target.value)}
          />
        </div>
        <div className="mb-4">
          <p className="m-0 mb-1">Extra_Info</p>
          <TextInput
            labelText="Extra_Info"
            hideLabel
            value={values.extraInfo}
            onChange={e => setField("extraInfo", e.target.value)}
          />
        </div>

        <div className="d-flex">
          <Button
            action={() => {
              if (isEditMode && existingAsset) {
                navigate(`/aims/AssetsView/preview/${existingAsset.id}`)
                return
              }
              navigate("/aims/AssetsView")
            }}
            label="Cancel"
            className="btn-full"
          />
          <Button
            action={() => {
              if (isSaveDisabled) return
              if (!validate()) return
              if (!isLocationReady) {
                showSnackBar(locationValidation.message || "Coordinates must be validated before saving.", "error")
                return
              }
              setShowConfirm(true)
            }}
            label={saveLabel}
            disabled={isSaveDisabled}
            className="btn-full green ml-2"
            style={{
              opacity: isSaveDisabled ? 0.65 : 1,
              pointerEvents: isSaveDisabled ? "none" : "auto",
            }}
          />
        </div>
      </div>

      <Modal
        open={showConfirm}
        modalHeading={isEditMode ? "Confirm Update" : "Confirm Create"}
        primaryButtonText={
          isCreating || isUpdating || isAttachingPolygon ? "Saving..." : isEditMode ? "Update" : "Create"
        }
        secondaryButtonText="Cancel"
        onRequestClose={() => setShowConfirm(false)}
        onSecondarySubmit={() => setShowConfirm(false)}
        onRequestSubmit={onSave}
        primaryButtonDisabled={isSaving || !isLocationReady}
      >
        <p>
          {isEditMode
            ? "Are you sure you want to update this asset with this information?"
            : "Are you sure you want to create this asset with this information?"}
        </p>
      </Modal>
    </div>
  )
}
